/**
 * LevelUpModal — the evolution pick, opened at each growth threshold.
 *
 * Three cards, each either a NEW body part or an UPGRADE of one already
 * grown (level n → n+1). Clicking a card resolves the pick; the part
 * mounts at its fixed spot on the fish, so there's no zone step.
 *
 * Lives in the stage's folder because it only knows about Cambrian parts.
 */

import { MAX_PART_LEVEL, PART_BY_ID, partIconSvg, partLevel, type PartId, type PartLevels } from './parts';

export class LevelUpModal {
  private root: HTMLElement | null = null;

  open(offers: PartId[], parts: PartLevels, evolution: number, onPick: (id: PartId) => void): void {
    this.injectStyle();
    const root = document.createElement('div');
    root.id = 'cambrian-levelup';
    root.innerHTML = `
      <div class="cl-scrim"></div>
      <div class="cl-modal">
        <div class="cl-header">
          <div class="cl-sub">EVOLUTION ${evolution}</div>
          <div class="cl-title">EVOLVE</div>
          <div class="cl-hint">Grow a new body part, or upgrade one you have.</div>
        </div>
        <div class="cl-cards"></div>
      </div>
    `;
    document.body.appendChild(root);
    this.root = root;
    const cards = root.querySelector('.cl-cards') as HTMLElement;
    let picked = false;
    for (const id of offers) {
      const card = this.buildCard(id, partLevel(parts, id));
      card.addEventListener('click', () => {
        if (picked) return;
        picked = true;
        this.close();
        onPick(id);
      });
      cards.appendChild(card);
    }
  }

  private buildCard(id: PartId, have: number): HTMLElement {
    const def = PART_BY_ID.get(id)!;
    const next = have + 1;
    const hex = `#${def.color.toString(16).padStart(6, '0')}`;
    const card = document.createElement('div');
    card.className = 'cl-card' + (have > 0 ? ' cl-upgrade' : '');
    card.style.setProperty('--mc', hex);
    const pips = Array.from({ length: MAX_PART_LEVEL }, (_, i) =>
      `<span class="cl-pip${i < have ? ' on' : i === have ? ' new' : ''}"></span>`).join('');
    const tag = have > 0 ? `UPGRADE · LV ${have} → ${next}` : 'NEW PART';
    card.innerHTML = `
      <div class="cl-card-tag">${tag}</div>
      <div class="cl-card-icon">${partIconSvg(id, def.color)}</div>
      <div class="cl-card-title">${def.name}</div>
      <div class="cl-card-spot">${def.spot}</div>
      <div class="cl-card-desc">${def.blurb}</div>
      <div class="cl-card-stats">${have > 0 ? `<span class="cl-was">${def.levels[have - 1]}</span>` : ''}<span class="cl-now">${def.levels[next - 1]}</span></div>
      <div class="cl-pips">${pips}</div>
    `;
    return card;
  }

  close(): void {
    this.root?.remove();
    this.root = null;
  }

  private injectStyle(): void {
    if (document.getElementById('cambrian-levelup-styles')) return;
    const style = document.createElement('style');
    style.id = 'cambrian-levelup-styles';
    style.textContent = `
#cambrian-levelup { position: fixed; inset: 0; z-index: 200; font-family: 'Inter', system-ui, sans-serif; color: #fff5dc; }
#cambrian-levelup .cl-scrim { position: absolute; inset: 0; background: rgba(8, 4, 0, 0.6); backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px); }
#cambrian-levelup .cl-modal { position: relative; max-width: 880px; margin: 6vh auto; padding: 28px 32px; border-radius: 18px; background: rgba(22, 8, 4, 0.92); border: 1px solid rgba(255, 200, 120, 0.25); box-shadow: 0 30px 60px rgba(0,0,0,0.6); }
#cambrian-levelup .cl-header { text-align: center; margin-bottom: 22px; }
#cambrian-levelup .cl-sub { font-size: 11px; letter-spacing: 0.4em; opacity: 0.55; }
#cambrian-levelup .cl-title { font-size: 32px; font-weight: 800; letter-spacing: 0.18em; color: #ffd070; text-shadow: 0 0 18px rgba(255, 190, 90, 0.4); }
#cambrian-levelup .cl-hint { font-size: 12px; opacity: 0.7; margin-top: 6px; letter-spacing: 0.1em; }
#cambrian-levelup .cl-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
#cambrian-levelup .cl-card { position: relative; background: rgba(14, 6, 4, 0.95); border: 1.5px solid var(--mc); border-radius: 14px; padding: 16px; cursor: pointer; transition: transform 0.15s, box-shadow 0.15s; display: flex; flex-direction: column; align-items: center; text-align: center; }
#cambrian-levelup .cl-card:hover { transform: translateY(-3px); box-shadow: 0 14px 30px rgba(0,0,0,0.45), 0 0 24px color-mix(in srgb, var(--mc) 35%, transparent); }
#cambrian-levelup .cl-card-tag { font-size: 9px; font-weight: 700; letter-spacing: 0.24em; color: var(--mc); }
#cambrian-levelup .cl-upgrade .cl-card-tag { color: #ffd070; }
#cambrian-levelup .cl-card-icon { width: 64px; height: 64px; margin: 12px 0 8px; border-radius: 50%; background: radial-gradient(circle at 50% 45%, color-mix(in srgb, var(--mc) 22%, transparent), rgba(0,0,0,0.3) 70%); box-shadow: 0 0 20px color-mix(in srgb, var(--mc) 40%, transparent); padding: 8px; box-sizing: border-box; }
#cambrian-levelup .cl-card-title { font-size: 17px; font-weight: 800; }
#cambrian-levelup .cl-card-spot { font-size: 9px; letter-spacing: 0.22em; text-transform: uppercase; opacity: 0.5; margin: 2px 0 8px; }
#cambrian-levelup .cl-card-desc { font-size: 11px; line-height: 1.45; opacity: 0.8; min-height: 32px; }
#cambrian-levelup .cl-card-stats { display: flex; flex-direction: column; gap: 4px; margin-top: 10px; font-size: 11px; width: 100%; }
#cambrian-levelup .cl-card-stats span { padding: 4px 6px; border-radius: 6px; }
#cambrian-levelup .cl-was { opacity: 0.45; text-decoration: line-through; background: rgba(255,255,255,0.03); }
#cambrian-levelup .cl-now { background: color-mix(in srgb, var(--mc) 16%, transparent); color: #fff; font-weight: 600; }
#cambrian-levelup .cl-pips { display: flex; gap: 6px; margin-top: 12px; }
#cambrian-levelup .cl-pip { width: 10px; height: 10px; border-radius: 50%; border: 1.5px solid var(--mc); opacity: 0.4; }
#cambrian-levelup .cl-pip.on { background: var(--mc); opacity: 1; }
#cambrian-levelup .cl-pip.new { background: #ffd070; border-color: #ffd070; opacity: 1; box-shadow: 0 0 8px #ffd070; animation: cl-pulse 0.9s ease-in-out infinite alternate; }
@media (max-width: 760px), (max-height: 500px) {
  #cambrian-levelup .cl-modal { margin: 2vh auto; padding: 16px; max-height: 96vh; overflow-y: auto; }
  #cambrian-levelup .cl-title { font-size: 24px; }
  #cambrian-levelup .cl-cards { grid-template-columns: 1fr; gap: 10px; }
  #cambrian-levelup .cl-card { flex-direction: row; text-align: left; align-items: center; gap: 12px; padding: 12px; flex-wrap: wrap; }
  #cambrian-levelup .cl-card-icon { width: 46px; height: 46px; margin: 0; flex: 0 0 46px; }
  #cambrian-levelup .cl-card-desc { min-height: 0; flex: 1 1 100%; }
  #cambrian-levelup .cl-card-stats { margin-top: 4px; }
  #cambrian-levelup .cl-pips { margin-top: 4px; }
}
@keyframes cl-pulse { from { transform: scale(0.85); } to { transform: scale(1.2); } }
`;
    document.head.appendChild(style);
  }
}
